const prompts = [
	["presenting", "What relational or behavioral difficulties bring the client to therapy?"],
	["pain", "What painful emotional experience or poignant moments emerge?"],
	["style", "How does the client process emotional experience?"],
	["narrative", "What emotion-based life story relates to attachment and identity?"],
	["markers", "Which markers for therapeutic task work are present?"],
	["schemes", "What underlying core emotion schemes appear? Describe adaptive or maladaptive elements where known."],
	["needs", "What needs are connected with these emotions?"],
	["secondary", "Which secondary emotions are present?"],
	["interruptions", "What interrupts or blocks access to core emotion schemes?"],
	["themes", "What self–self, self–other, or existential themes emerge?"],
	["formulation", "How do the presenting difficulties, triggering events, and core emotion schemes connect?"],
	["emerging", "What task markers emerge during treatment?"],
	["micro", "What micro-markers occur within the work?"],
	["meaning", "How does emerging meaning reshape the narrative and connect back to presenting problems?"],
]

export function createEftCaseFormulationConfig() {
	return {
		title: "EFT Case Formulation",
		instructions: "Build a provisional treatment map. Record what the client reported separately from your interpretation, and revise the map as new emotional process information emerges.",
		showPriorResults: true,
		stages: [
			{id: "narrative", title: "Stage 1 · Narrative and emotional processing", description: "Presenting problems and observed emotional processing", itemPlaceholder: "Presenting difficulty", allowAlternatives: false, showStatus: false},
			{id: "focus", title: "Stage 2 · Focus and core emotion", description: "Connect difficulty, emotional process, marker, target, and possible task", itemPlaceholder: "Summarize the formulation link", allowAlternatives: false, showStatus: true, itemFields: [
				{id: "difficultyRef", title: "Presenting difficulty", responseType: "reference", referenceStageId: "narrative"},
				{id: "trigger", title: "Reported triggering event or evidence", responseType: "multiline"},
				{id: "process", title: "Emotional process or scheme", responseType: "multiline"},
				{id: "marker", title: "Marker or process difficulty", responseType: "text"},
				{id: "target", title: "Priority change target", responseType: "text"},
				{id: "task", title: "Likely task or change process", responseType: "text"},
				{id: "implication", title: "Session-planning implication", responseType: "multiline"},
			]},
			{id: "process", title: "Stage 3 · Process and new meaning", description: "Emerging markers and changes in meaning", itemPlaceholder: "Describe an emerging change", allowAlternatives: false, showStatus: false},
		],
		contextMap: {
			focusLabel: "Presenting issues",
			focusPlaceholder: "Summarize the client's presenting concerns",
			effectsLabel: "Client-reported material and clinical evidence",
			effectsPlaceholder: "What did the client report or show?",
			reflectionLabel: "Session-planning implications",
			reflectionPlaceholder: "What direction seems useful for the next session?",
			linkPlaceholder: "Describe a formulation link",
			alternativesTitle: "Priority change targets, in order",
			alternativePlaceholder: "Change target",
			promptsTitle: "Formulation questions",
			unfilledLabel: "Unresolved",
			coverageLabel: "Stages with entries",
			alternativesCountLabel: "Priority targets",
			statusHeading: "Certainty",
			addLinkLabel: "Add entry",
			addAlternativeLabel: "Add target",
			archivedTitle: "Earlier formulation areas",
			gapsTitle: "Unresolved formulation questions",
			historyTitle: "Formulation revisions",
			heldMessage: "This formulation is held by the practitioner.",
			privateNoteLabel: "Practitioner note · private from client",
			visibility: "practitioner",
			statusOptions: [
				{value: "identified", label: "Identified"},
				{value: "tentative", label: "Tentative"},
				{value: "unknown", label: "Unknown"},
			],
			prompts: prompts.map(([id, text], index) => ({id, text, stageId: index < 4 ? "narrative" : index < 11 ? "focus" : "process"})),
		},
	}
}

export function createEftCaseFormulationDemoPayload() {
	return {stagedWorksheet: {
		context: "Mara feels unable to speak up with her partner and often withdraws after criticism.",
		reaction: "Mara described a recent argument about plans for the weekend and said she felt small and alone.",
		theme: "Explore the self-critical response and the need to feel understood before choosing a task in session.",
		stages: [
			{id: "narrative", items: [{id: "difficulty_1", text: "Withdraws after criticism"}, {id: "difficulty_2", text: "Struggles to express needs with her partner"}]},
			{id: "focus", items: [{id: "link_1", text: "Criticism, shame, and withdrawal", values: {statusId: "tentative", difficultyRef: "difficulty_1", trigger: "A disagreement about weekend plans", process: "A core shame response may be present", marker: "Self-critical statements", target: "Stay with the primary feeling beneath self-criticism", task: "Two-chair work may be useful", implication: "Explore this marker if it arises in session"}}]},
		],
		alternatives: [{id: "target_1", text: "Stay with the primary feeling beneath self-criticism"}],
		promptAnswers: {schemes: "Shame appears when Mara expects criticism from someone close.", markers: "Self-critical statements arise quickly after describing conflict.", needs: "Validation and acceptance.", formulation: "The argument triggered shame and withdrawal, which may protect Mara from further criticism."},
	}}
}
